import * as React from "react";
import { mergeClasses } from "@fluentui/react-components";
import {
  Home20Filled,
  VehicleCar20Filled,
  PersonAccounts20Filled,
} from "@fluentui/react-icons";
import ChoiceCard from "../../schadenmeldung/ChoiceCard";
import { useStepStyles } from "../../schadenmeldung/steps/stepStyles";
import { BIG_COPY } from "../copy";
import {
  type BigDamageType,
  type BigStepProps,
  toggleValue,
} from "../types";
import {
  StepHead,
  SubHead,
  Block,
  FieldGrid,
  TextField,
  RadioGroup,
  CheckboxList,
  ContactBlock,
  InfoBox,
} from "../fields";

// ---- Step 1: damage type ----------------------------------------------------
export function StepType({ answers, update }: BigStepProps) {
  const s = useStepStyles();
  const C = BIG_COPY.type;
  const pick = (t: BigDamageType) => {
    if (answers.damageType === t) return;
    update({ damageType: t });
  };
  return (
    <div>
      <StepHead eyebrow={C.eyebrow} title={C.title} hint={C.hint} />
      <div className={s.grid3}>
        <ChoiceCard
          size="lg"
          icon={<Home20Filled style={{ fontSize: 28 }} />}
          label={C.options.gebaeude.label}
          helper={C.options.gebaeude.helper}
          selected={answers.damageType === "gebaeude"}
          onClick={() => pick("gebaeude")}
        />
        <ChoiceCard
          size="lg"
          icon={<VehicleCar20Filled style={{ fontSize: 28 }} />}
          label={C.options.kfz.label}
          helper={C.options.kfz.helper}
          selected={answers.damageType === "kfz"}
          onClick={() => pick("kfz")}
        />
        <ChoiceCard
          size="lg"
          icon={<PersonAccounts20Filled style={{ fontSize: 28 }} />}
          label={C.options.sonstiges.label}
          helper={C.options.sonstiges.helper}
          selected={answers.damageType === "sonstiges"}
          onClick={() => pick("sonstiges")}
        />
      </div>
    </div>
  );
}

// ---- Schadenmelder + Ansprechpartner (shared across branches) ---------------
// `ohneFuerWen`/`hint` nutzt nur die Schadenmappe (intern): dort steht durch
// die Kontaktwahl schon fest, für wen gemeldet wird. Der öffentliche Wizard
// übergibt beides nicht und bleibt unverändert.
export function MelderStep({
  answers,
  update,
  ohneFuerWen = false,
  hint,
}: BigStepProps & { ohneFuerWen?: boolean; hint?: string }) {
  const C = BIG_COPY.melder;
  return (
    <div>
      <StepHead eyebrow={C.eyebrow} title={C.title} hint={hint ?? C.hint} />
      <Block>
        <FieldGrid>
          <TextField label={C.vorname} value={answers.melderVorname} onChange={(v) => update({ melderVorname: v })} autoComplete="given-name" />
          <TextField label={C.name} value={answers.melderName} onChange={(v) => update({ melderName: v })} autoComplete="family-name" />
          <TextField label={C.tel} value={answers.melderTel} onChange={(v) => update({ melderTel: v })} type="tel" inputMode="tel" autoComplete="tel" />
          <TextField label={C.handy} value={answers.melderHandy} onChange={(v) => update({ melderHandy: v })} type="tel" inputMode="tel" />
          <TextField label={C.email} value={answers.melderEmail} onChange={(v) => update({ melderEmail: v })} type="email" inputMode="email" autoComplete="email" />
          <TextField label={C.kuerzel} value={answers.melderKuerzel} onChange={(v) => update({ melderKuerzel: v })} optional />
          {!ohneFuerWen && (
            <TextField label={C.fuerUnternehmen} value={answers.melderFuerUnternehmen} onChange={(v) => update({ melderFuerUnternehmen: v })} placeholder={C.fuerUnternehmenPlaceholder} />
          )}
        </FieldGrid>
        <TextField label={C.vorgangsnummer} value={answers.melderVorgangsnummer} onChange={(v) => update({ melderVorgangsnummer: v })} optional />
      </Block>

      <SubHead title={C.apTitle} />
      <Block>
        <RadioGroup options={C.apOptions} value={answers.apWer} onChange={(v) => update({ apWer: v })} />
        {answers.apWer === "andere" && (
          <ContactBlock
            value={answers.apContact}
            onChange={(patch) => update({ apContact: { ...answers.apContact, ...patch } })}
            show={{ funktion: true, kuerzel: true, emailOptional: true }}
          />
        )}
      </Block>
    </div>
  );
}

// ---- Polizeiliche Meldung (shared zwischen Gebäude & KFZ) -------------------
export function PolizeiBlock({
  polizei,
  aktenzeichen,
  dienststelle,
  onChange,
  showInfo,
}: {
  polizei: string | null;
  aktenzeichen: string;
  dienststelle: string;
  onChange: (patch: {
    polizei?: string;
    aktenzeichen?: string;
    dienststelle?: string;
  }) => void;
  showInfo?: boolean;
}) {
  const C = BIG_COPY.gebVerursacher;
  return (
    <>
      <SubHead title={C.polizeiTitle} />
      <Block>
        {showInfo && (
          <InfoBox summary="Wann ist eine polizeiliche Meldung nötig?">{C.polizeiInfo}</InfoBox>
        )}
        <RadioGroup label={C.polizeiLabel} options={C.polizeiOptions} value={polizei} onChange={(v) => onChange({ polizei: v })} columns />
        {polizei === "ja" && (
          <FieldGrid>
            <TextField label={C.aktenzeichen} value={aktenzeichen} onChange={(v) => onChange({ aktenzeichen: v })} optional />
            <TextField label={C.dienststelle} value={dienststelle} onChange={(v) => onChange({ dienststelle: v })} optional />
          </FieldGrid>
        )}
      </Block>
    </>
  );
}

// ---- Schadentag (shared) ----------------------------------------------------
function toLocalInputValue(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function WhenStep({ answers, update }: BigStepProps) {
  const s = useStepStyles();
  const C = BIG_COPY.when;
  const setQuick = (kind: "now" | "today" | "yesterday" | "week") => {
    const d = new Date();
    if (kind === "today") d.setHours(9, 0, 0, 0);
    else if (kind === "yesterday") {
      d.setDate(d.getDate() - 1);
      d.setHours(12, 0, 0, 0);
    } else if (kind === "week") {
      d.setDate(d.getDate() - 3);
      d.setHours(12, 0, 0, 0);
    }
    update({ schadenDatum: toLocalInputValue(d) });
  };
  const max = toLocalInputValue(new Date());
  return (
    <div>
      <StepHead eyebrow={C.eyebrow} title={C.title} hint={C.hint} />
      <div className={s.chipsRow} style={{ marginBottom: 18 }}>
        <button type="button" className={s.chip} onClick={() => setQuick("now")}>{C.chips.now}</button>
        <button type="button" className={s.chip} onClick={() => setQuick("today")}>{C.chips.today}</button>
        <button type="button" className={s.chip} onClick={() => setQuick("yesterday")}>{C.chips.yesterday}</button>
        <button type="button" className={s.chip} onClick={() => setQuick("week")}>{C.chips.week}</button>
      </div>
      <Block>
        <div className={s.field} style={{ maxWidth: 360 }}>
          <label className={s.label}>{C.datum}</label>
          <input className={s.input} type="datetime-local" value={answers.schadenDatum} max={max} onChange={(e) => update({ schadenDatum: e.target.value })} />
        </div>
        <TextField label={C.zeitpunkt} value={answers.schadenZeitpunkt} onChange={(v) => update({ schadenZeitpunkt: v })} optional placeholder="z. B. vormittags, am Wochenende …" />
        <TextField label={C.entdeckt} value={answers.entdecktAm} onChange={(v) => update({ entdecktAm: v })} optional />
      </Block>
    </div>
  );
}

// ---- Unterlagen (shared, options by branch) ---------------------------------
export function UnterlagenStep({ answers, update }: BigStepProps) {
  const C = BIG_COPY.unterlagen;
  const t = answers.damageType;
  // KFZ: nur Unterlagen anzeigen, die zur gewählten Schadenkategorie passen.
  const kfzOptions = C.kfzOptions.filter((o) => {
    const req = C.kfzOptionRequires[o.value];
    return !req || req.some((k) => answers.kfzSchaeden.includes(k));
  });
  const options =
    t === "gebaeude" ? C.gebOptions : t === "kfz" ? kfzOptions : C.sonOptions;
  const values =
    t === "gebaeude" ? answers.gebUnterlagen : t === "kfz" ? answers.kfzUnterlagen : answers.sonUnterlagen;
  const key =
    t === "gebaeude" ? "gebUnterlagen" : t === "kfz" ? "kfzUnterlagen" : "sonUnterlagen";
  const onToggle = (v: string) => update({ [key]: toggleValue(values, v) });
  const extra = t === "gebaeude" && answers.gebSchadenart ? C.gebExtra[answers.gebSchadenart] : null;
  return (
    <div>
      <StepHead eyebrow={C.eyebrow} title={C.title} hint={C.hint} />
      <Block>
        <p style={{ margin: 0, color: "#6c6171", fontSize: "0.9rem" }}>{C.note}</p>
        {t === "gebaeude" && (
          <p style={{ margin: 0, color: "#6c6171", fontSize: "0.86rem" }}>{C.fotosHint}</p>
        )}
        <CheckboxList options={options} values={values} onToggle={onToggle} columns />
        {extra && (
          <p style={{ margin: 0, color: "#6c6171", fontSize: "0.86rem" }}>
            <strong>Zusätzlich sinnvoll:</strong> {extra}
          </p>
        )}
      </Block>
    </div>
  );
}

// ---- Abschluss: Kopie + Einwilligung ---------------------------------------
export function AbschlussStep({ answers, update }: BigStepProps) {
  const s = useStepStyles();
  const C = BIG_COPY.abschluss;
  return (
    <div>
      <StepHead eyebrow={C.eyebrow} title={C.title} hint={C.hint} />
      <Block>
        <RadioGroup label={C.kopieLabel} options={C.kopieOptions} value={answers.kopie} onChange={(v) => update({ kopie: v })} columns />
        <label className={mergeClasses(s.consentRow)} style={{ marginTop: 8 }}>
          <input className={s.consentCheckbox} type="checkbox" checked={answers.consentGiven} onChange={(e) => update({ consentGiven: e.target.checked })} />
          <span>{C.consent}</span>
        </label>
      </Block>
    </div>
  );
}
